import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, MapPin, Mail, Phone, CheckCircle } from 'lucide-react';
import { contactData } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Discovery Request Submitted!",
          description: "We'll contact you within 24 hours to schedule your site walk.",
          duration: 5000,
        });
        setFormData({});
        e.target.reset();
      } else {
        toast({
          title: "Submission Failed",
          description: data.detail || "Please try again later.",
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to submit form. Please try again later.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    // Open the PDF in a new tab for download
    const assetsUrl = process.env.REACT_APP_ASSETS_URL || 'https://customer-assets.emergentagent.com';
    window.open(`${assetsUrl}/job_impact-zones/artifacts/j07ayooz_Digital%20Detox%20Executive%20.pdf`, '_blank');
    
    toast({
      title: "Download Started",
      description: "Your Digital Detox Executive guide is ready!",
      duration: 3000,
    });
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Contact | Request a Site Discovery Walk | Legacy & Landscapes</title>
        <meta name="description" content="Schedule your free Site Discovery Walk. We'll assess your property's glamping potential with our AI-driven feasibility lens. Contact us today." />
        <link rel="canonical" href="https://goglamping.co.za/contact" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-stone-100 to-sage-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6">
              {contactData.title}
            </h1>
            <p className="text-2xl text-sage-800 font-semibold mb-6">
              {contactData.subtitle}
            </p>
            <p className="text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed">
              {contactData.description}
            </p>
          </div>

          {/* Lead Magnet */}
          <div className="max-w-2xl mx-auto bg-sage-700 text-white p-8 rounded-2xl shadow-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-2xl font-bold mb-2">Free Resource</h3>
                <p className="text-sage-100">{contactData.leadMagnet}</p>
              </div>
              <Button
                onClick={handleDownload}
                size="lg"
                className="bg-white text-sage-700 hover:bg-gray-100 px-6 py-5 text-base whitespace-nowrap"
              >
                <Download className="mr-2 h-5 w-5" />
                Download Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold text-stone-900 mb-6">
                Request Your Site Discovery Walk
              </h2>
              <p className="text-lg text-stone-600 mb-8">
                Fill out the form below and we'll schedule a consultation to explore your land's potential.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {contactData.formFields.map((field) => (
                  <div key={field.id}>
                    <Label htmlFor={field.name} className="text-stone-900 font-medium mb-2 block">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.name}
                        name={field.name}
                        placeholder={field.placeholder}
                        required={field.required}
                        onChange={handleChange}
                        className="min-h-[120px] border-stone-300 focus:border-sage-700 focus:ring-sage-700"
                      />
                    ) : (
                      <Input
                        id={field.name}
                        name={field.name}
                        type={field.type}
                        placeholder={field.placeholder}
                        required={field.required}
                        onChange={handleChange}
                        className="border-stone-300 focus:border-sage-700 focus:ring-sage-700"
                      />
                    )}
                  </div>
                ))}

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-sage-700 hover:bg-sage-800 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Request Site Discovery Walk
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl font-bold text-stone-900 mb-6">
                  Get in Touch
                </h2>
                <p className="text-lg text-stone-600 mb-8">
                  We're here to answer your questions and help you unlock your land's potential.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="bg-sage-700 p-3 rounded-lg">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900 mb-1">Email Us</h3>
                      <a
                        href="mailto:steve@goglamping.co.za"
                        className="text-sage-700 hover:text-sage-800"
                      >
                        steve@goglamping.co.za
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="bg-sage-700 p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900 mb-1">Call Us</h3>
                      <a
                        href="tel:+27083449722"
                        className="text-sage-700 hover:text-sage-800"
                      >
                        +27 083 449 7722
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="bg-sage-700 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900 mb-1">Visit Us</h3>
                      <p className="text-stone-600">
                        Knysna, Garden Route, South Africa
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Work With Us */}
              <div className="bg-sage-700 text-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Why Work With Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 mt-1 flex-shrink-0" />
                    <span>No upfront commitment required—just a discovery walk</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 mt-1 flex-shrink-0" />
                    <span>AI-driven feasibility analysis of your land</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 mt-1 flex-shrink-0" />
                    <span>Proven track record with landowners across South Africa</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 mt-1 flex-shrink-0" />
                    <span>Complete support from design to guest management</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-stone-900 text-center mb-12">
            Common Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                What happens during the site discovery walk?
              </h3>
              <p className="text-stone-600">
                We'll walk your property together, identify the most scenic and accessible areas, assess infrastructure needs, and provide an initial feasibility assessment—all at no cost or obligation.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                How much land do I need?
              </h3>
              <p className="text-stone-600">
                Each glamping unit requires approximately 60-100 square meters of footprint, plus surrounding buffer space. We can work with as little as 1 hectare or as much as you'd like to dedicate.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                What's the investment required?
              </h3>
              <p className="text-stone-600">
                Each eco-sanctuary pod represents an investment from R500 000. We offer various partnership models including full ownership, revenue sharing, and management agreements to suit your goals.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                Will this disrupt my current operations?
              </h3>
              <p className="text-stone-600">
                Not at all. The beauty of this model is its discreet footprint. Guest access is carefully managed to designated areas only, allowing your primary operations and privacy to continue undisturbed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-stone-900 to-sage-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Your Land's Transformation Begins Here
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Join the landowners who are already building their legacy through beauty, not development
          </p>
          <p className="text-2xl font-semibold text-sage-300">
            "Don't just own the view. Share it, preserve it, and let it build your family's next chapter."
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
